import { useEffect, useState } from "react";
import {
  addCompanyExternalId,
  createOrUpdateCompany,
  deleteCompany,
  fetchCompanies,
  patchCompany,
  removeCompanyExternalId,
  renameCompany,
  type Company,
} from "../api";

export function Companies() {
  const [items, setItems] = useState<Company[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<{ id: string; name: string; alias: string; notes: string }>({
    id: "",
    name: "",
    alias: "",
    notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setError(null);
    fetchCompanies()
      .then((r) => setItems(r.items))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  };

  useEffect(load, []);

  const save = async () => {
    if (!draft.id.trim() || !draft.name.trim()) {
      alert("Indica al menos NIF/CIF y Nombre.");
      return;
    }
    setBusy(true);
    try {
      await createOrUpdateCompany({
        id: draft.id.trim(),
        name: draft.name.trim(),
        alias: draft.alias.trim() || undefined,
        notes: draft.notes.trim() || undefined,
      });
      setDraft({ id: "", name: "", alias: "", notes: "" });
      setCreating(false);
      load();
    } catch (e: unknown) {
      alert(`Error al crear: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Empresas</h2>
          <p className="meta">
            Da de alta aquí las empresas del grupo (NIF/CIF + nombre). Después
            podrás vincular cada cuenta y préstamo a su empresa propietaria.
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={() => setCreating((x) => !x)}>
            {creating ? "✕ Cancelar" : "+ Nueva empresa"}
          </button>
          <button type="button" onClick={load}>Recargar</button>
        </div>
      </div>

      {creating && (
        <div className="form-row">
          <input
            placeholder="NIF / CIF"
            value={draft.id}
            onChange={(e) => setDraft({ ...draft, id: e.target.value })}
          />
          <input
            placeholder="Razón social"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
          <input
            placeholder="Alias (opcional)"
            value={draft.alias}
            onChange={(e) => setDraft({ ...draft, alias: e.target.value })}
          />
          <input
            placeholder="Notas"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          />
          <button type="button" onClick={save} disabled={busy}>
            {busy ? "Guardando…" : "Guardar"}
          </button>
        </div>
      )}

      {error && <p className="error">{error}</p>}
      {items === null && !error && <p>Cargando…</p>}
      {items && items.length === 0 && <p className="meta">Aún no hay empresas. Crea la primera con el botón "+ Nueva empresa".</p>}
      {items && items.length > 0 && (
        <table className="data">
          <thead>
            <tr>
              <th>NIF/CIF</th>
              <th>Nombre</th>
              <th>Alias</th>
              <th>Tipo</th>
              <th>SAP</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <CompanyRow key={c.id} company={c} onUpdated={load} />
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function CompanyRow({ company, onUpdated }: { company: Company; onUpdated: () => void }) {
  const [editing, setEditing] = useState(false);
  const [alias, setAlias] = useState(company.alias);
  const [notes, setNotes] = useState(company.notes);
  const [busy, setBusy] = useState(false);
  const [extDraft, setExtDraft] = useState("");
  const [editingId, setEditingId] = useState(false);
  const [newIdDraft, setNewIdDraft] = useState(company.id);

  const save = async () => {
    setBusy(true);
    try {
      await patchCompany(company.id, { alias, notes });
      setEditing(false);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const addExt = async () => {
    const v = extDraft.trim();
    if (!v) return;
    setBusy(true);
    try {
      await addCompanyExternalId(company.id, v);
      setExtDraft("");
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const removeExt = async (v: string) => {
    setBusy(true);
    try {
      await removeCompanyExternalId(company.id, v);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const saveRename = async () => {
    const newId = newIdDraft.trim();
    if (!newId || newId === company.id) {
      setEditingId(false);
      setNewIdDraft(company.id);
      return;
    }
    if (!confirm(
      `Cambiar NIF/CIF de "${company.id}" a "${newId}"?\n\n` +
        `· El id viejo (${company.id}) se conservará en "IDs alternativos" para no romper el matching con payloads anteriores.\n` +
        `· Las cuentas y préstamos vinculados se reasignarán automáticamente al nuevo id.`,
    )) return;
    setBusy(true);
    try {
      const r = await renameCompany(company.id, newId);
      alert(`Renombrado OK.\nReasignadas: ${r.reassigned.accounts} cuentas, ${r.reassigned.loans} préstamos.`);
      setEditingId(false);
      onUpdated();
    } catch (e: unknown) {
      alert(`Error al renombrar: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!confirm(`Borrar la empresa "${company.name}" (${company.id})?`)) return;
    setBusy(true);
    try {
      const r = await deleteCompany(company.id, false);
      if (r.ok) {
        onUpdated();
        return;
      }
      // 409: tiene dependencias. Mostrar y ofrecer force.
      const { dependencies } = r;
      const msg =
        `Esta empresa tiene asociados:\n` +
        `  · ${dependencies.accounts.length} cuentas\n` +
        `  · ${dependencies.loans.length} préstamos\n\n` +
        `¿Quieres BORRARLA igualmente? Las cuentas y préstamos quedarán sin empresa (companyId vacío) y podrás reasignarlos luego.`;
      if (!confirm(msg)) return;
      const forced = await deleteCompany(company.id, true);
      if (forced.ok) {
        onUpdated();
      }
    } catch (e: unknown) {
      alert(`Error al borrar: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        {editingId ? (
          <span style={{ display: "inline-flex", gap: "0.25rem" }}>
            <input
              autoFocus
              value={newIdDraft}
              onChange={(e) => setNewIdDraft(e.target.value)}
              style={{ width: "9rem" }}
            />
            <button type="button" onClick={saveRename} disabled={busy}>OK</button>
            <button
              type="button"
              onClick={() => { setEditingId(false); setNewIdDraft(company.id); }}
              disabled={busy}
            >
              ✕
            </button>
          </span>
        ) : (
          <span
            onClick={() => setEditingId(true)}
            style={{ cursor: "pointer" }}
            title="Click para cambiar el NIF/CIF"
          >
            <code>{company.id}</code> <span className="meta" style={{ fontSize: "0.75em" }}>✏️</span>
          </span>
        )}
      </td>
      <td>
        {company.name}
        {editing && (
          <>
            <div style={{ marginTop: "0.25rem" }}>
              <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notas" style={{ width: "100%" }} />
            </div>
            <div style={{ marginTop: "0.5rem" }}>
              <div className="meta" style={{ fontSize: "0.75em", marginBottom: "0.2rem" }}>
                IDs alternativos (bancos) — sólo si un pseudo-ID llega distinto al CIF real:
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "0.25rem", alignItems: "center" }}>
                {company.externalIds.length === 0 && <span className="meta">—</span>}
                {company.externalIds.map((eid) => (
                  <span key={eid} className="badge" style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                    <code>{eid}</code>
                    <button
                      type="button"
                      onClick={() => removeExt(eid)}
                      disabled={busy}
                      title="Quitar este ID"
                      style={{ padding: "0 0.25rem", lineHeight: 1, border: "none", background: "transparent", color: "#b91c1c", cursor: "pointer" }}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <span style={{ display: "inline-flex", gap: "0.25rem" }}>
                  <input
                    placeholder="J0002…"
                    value={extDraft}
                    onChange={(e) => setExtDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") addExt(); }}
                    style={{ width: "8rem" }}
                  />
                  <button type="button" onClick={addExt} disabled={busy || !extDraft.trim()}>+</button>
                </span>
              </div>
            </div>
          </>
        )}
        {!editing && company.notes && <div className="meta" style={{ fontSize: "0.85em" }}>{company.notes}</div>}
      </td>
      <td>
        {editing ? (
          <input value={alias} onChange={(e) => setAlias(e.target.value)} style={{ width: "100%" }} />
        ) : (
          company.alias || <span className="meta">—</span>
        )}
      </td>
      <td>{company.isCompany ? "🏢" : "👤"}</td>
      <td>
        {company.sap ? (
          <span title={`Usuario: ${company.sap.username}`}>
            ✅ <code>{company.sap.companyDb}</code>
          </span>
        ) : (
          <a href="/sap" className="meta" title="Configurar conexión SAP">— configurar</a>
        )}
      </td>
      <td className="actions">
        {editing ? (
          <>
            <button type="button" onClick={save} disabled={busy}>OK</button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setAlias(company.alias);
                setNotes(company.notes);
              }}
              disabled={busy}
            >
              ✕
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setEditing(true)} disabled={busy}>✏️</button>
            <button type="button" className="danger" onClick={onDelete} disabled={busy}>🗑️</button>
          </>
        )}
      </td>
    </tr>
  );
}
